import { Calendar, Music, Building2 } from "lucide-react";
import { eventsApi, artistsApi, venuesApi } from "../../utils/api";

// Shared between the reports list (index.jsx) and the create modal
// (CreateReportModal.jsx). They live here rather than in index.jsx because
// index.jsx imports the modal — importing them back would be a cycle.

export const REASON_LABELS = {
  incorrect_info: "Incorrect info",
  duplicate: "Duplicate",
  spam: "Spam",
  other: "Other",
  missing_ticketing: "Missing ticketing",
  multiple_links_provider: "Multiple provider links",
};

// Mirrors SYSTEM_REPORT_REASONS in apps/api/shared/schemas/reports.py. These are
// created only by ingestion and the local agent workers: they carry dedupe,
// agent_proposal and self-heal invariants that a hand-created report would break,
// and POST /reports rejects them with 400. So they stay filterable and displayable
// but are never offered when creating one.
const SYSTEM_REPORT_REASONS = new Set([
  "missing_ticketing",
  "multiple_links_provider",
]);

// Per-entity-type registry. `fetchName` resolves an entity's display name for the
// detail modal (each GET /admin/{type}s/{id} wraps the entity as { <type>: {...} }).
// `search`/`listKey`/`optionLabel`/`optionSublabel` back the create modal's entity
// picker — every admin list `search` also matches the entity's own exact ID, so
// pasting an ID resolves it to the one row. The link to each entity's detail modal
// comes from the shared getAdminEntityUrl helper.
export const ENTITY_TYPE_META = {
  event: {
    label: "Event",
    icon: Calendar,
    fetchName: async (id) => (await eventsApi.getById(id))?.event?.title,
    search: (query, config) => eventsApi.getAll({ search: query, page_size: 8 }, config),
    listKey: "events",
    optionLabel: (e) => e.title,
    optionSublabel: (e) => e.venue?.name || e.venue_name || "",
  },
  artist: {
    label: "Artist",
    icon: Music,
    fetchName: async (id) => (await artistsApi.getById(id))?.artist?.name,
    search: (query, config) => artistsApi.getAll({ search: query, page_size: 8 }, config),
    listKey: "artists",
    optionLabel: (a) => a.name,
    optionSublabel: (a) => (a.country || "").toUpperCase(),
  },
  venue: {
    label: "Venue",
    icon: Building2,
    fetchName: async (id) => (await venuesApi.getById(id))?.venue?.name,
    search: (query, config) => venuesApi.getAll({ search: query, page_size: 8 }, config),
    listKey: "venues",
    optionLabel: (v) => v.name,
    optionSublabel: (v) => v.city || "",
  },
};

export const entityTypeSelectOptions = Object.entries(ENTITY_TYPE_META).map(
  ([value, meta]) => ({ value, label: meta.label })
);

// Column-filter option lists. "" is the "no filter" value every column filter uses
// (TableColumnHeader dims the icon when the value is empty).
export const entityTypeFilterOptions = [
  { value: "", label: "All entity types" },
  ...entityTypeSelectOptions,
];

// Derived from REASON_LABELS (like entityTypeFilterOptions from ENTITY_TYPE_META) so a new
// report reason only has to be added in one place. The filter offers every reason —
// system-generated reports exist and must stay findable.
export const reasonFilterOptions = [
  { value: "", label: "All reasons" },
  ...Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label })),
];

// The create form offers only the reasons a human may file.
export const createReasonOptions = Object.entries(REASON_LABELS)
  .filter(([value]) => !SYSTEM_REPORT_REASONS.has(value))
  .map(([value, label]) => ({ value, label }));
